import React, { useState, useMemo } from 'react';
import { IPavementData } from '../../store/ducks/fabrication-mockups/types';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { Select } from '@gerdau/hefesto';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { Container, SelectContainer } from './styles';

interface IFabricationPavementSelectProps {
  pavementsOptions: IPavementData[];
  onChange: (pavementIds: number[]) => void;
  label?: string;
  pavements: number[];
  isPavementRangeNeeded: boolean;
  disabled: boolean;
}

const FabricationPavementSelect: React.FC<IFabricationPavementSelectProps> = ({
  pavementsOptions,
  onChange,
  label,
  pavements,
  isPavementRangeNeeded,
  disabled
}) => {
  const [canShowRangeError, setCanShowRangeError] = useState(false);

  const pavementsSelected = useMemo(
    () => pavements.map((p) => p.toString()),
    [pavements]
  );

  const getPavementsOptions = (): TSelectOptionProps[] => {
    const options = pavementsOptions.map(({ id, name }) => ({
      label: name,
      value: id.toString()
    }));

    return options;
  };

  const pavementsOptionsFormatted = useMemo(
    () => getPavementsOptions(),
    [pavementsOptions]
  );

  const getSelectedPavements = (ids: number[]) => {
    if (pavementsOptions) {
      return pavementsOptions
        .filter((p) => ids.includes(Number(p.id)))
        .sort((a, b) => a.position - b.position);
    } else {
      return [];
    }
  };

  const onSelectPavement = (selectedPavementIds: number[]) => {
    if (
      isPavementRangeNeeded &&
      selectedPavementIds.length > 1 &&
      pavementsOptions
    ) {
      const selectedPavements = getSelectedPavements(selectedPavementIds);
      const firstPosition = selectedPavements[0].position;
      const lastPosition =
        selectedPavements[selectedPavements.length - 1].position;
      const rangeOfSelectedPavements: { id: number; position: number }[] = [];
      pavementsOptions.forEach((p) => {
        if (p.position >= firstPosition && p.position <= lastPosition) {
          rangeOfSelectedPavements.push({
            id: Number(p.id),
            position: p.position
          });
        }
      });
      return onChange(
        rangeOfSelectedPavements
          .sort((p1, p2) => p1.position - p2.position)
          .map((p) => p.id)
      );
    }
    return onChange(selectedPavementIds);
  };

  return (
    <>
      {!isPavementRangeNeeded ? (
        <Select
          label={label ?? ''}
          name="selectPavement"
          hideLabel={!label}
          placeholder="Selecione o(s) pavimento(s)"
          disabled={pavementsOptions.length === 0 || disabled}
          isMultipleSelect
          value={pavementsSelected}
          options={pavementsOptionsFormatted}
          variant={canShowRangeError ? 'danger' : undefined}
          onFocus={() => setCanShowRangeError(false)}
          onSelect={(selectedOptions) => {
            const optionsIds = (selectedOptions as string[]).map((option) =>
              Number(option)
            );
            onSelectPavement(optionsIds);
            if (isPavementRangeNeeded && pavements.length > 1) {
              onChange(optionsIds);
            }
          }}
        ></Select>
      ) : (
        <Container>
          {label && (
            <KeyValueItem
              title={label}
              helperText="Para projetos de estrutura Pilar ou Escada, é
        necessário selecionar no mínimo 2 pavimentos."
              titleLevel={6}
            >
              {''}
            </KeyValueItem>
          )}
          <SelectContainer>
            <Select
              label=""
              name="selectPavementStart"
              hideLabel
              placeholder="Início"
              value={pavementsSelected[0]}
              disabled={pavementsOptions.length === 0 || disabled}
              options={pavementsOptionsFormatted}
              variant={pavementsSelected.length === 1 ? 'danger' : undefined}
              onSelect={(pavementStart) =>
                onSelectPavement([Number(pavementStart)])
              }
            ></Select>
            {'>'}
            <Select
              label=""
              name="selectPavementEnd"
              hideLabel
              placeholder="Fim"
              disabled={
                pavementsOptions.length === 0 || disabled || !pavements.length
              }
              value={
                pavementsSelected.length > 1
                  ? pavementsSelected[pavementsSelected.length - 1]
                  : undefined
              }
              options={pavementsOptionsFormatted.filter(
                (option) => option.value !== pavements[0]?.toString()
              )}
              variant={pavementsSelected.length === 1 ? 'danger' : undefined}
              onSelect={(pavementEnd) => {
                if (!pavements.length) return;
                const pavementsIds = [pavements[0], Number(pavementEnd)];
                onSelectPavement(pavementsIds);
              }}
            ></Select>
          </SelectContainer>
        </Container>
      )}
    </>
  );
};

export default FabricationPavementSelect;
